import {Button} from 'antd'
import {timetable_getMilestonesByCourseClass_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import classNames from 'classnames'
import dayjs from 'dayjs'
import _ from 'lodash'
import {formatDate} from 'helpers/datetimeHelpers'
import useTranslate from 'modules/local/useTranslate'
import useAsync from 'modules/asyncCache/useAsync'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {BsChevronDown} from 'react-icons/bs'
import {PiCalendarBlankFill, PiLinkBold, PiListBulletsBold} from 'react-icons/pi'
import {TimetableActionTypes} from 'views/AppLocals/Course'
import EntityList from 'views/AppLocals/EntityList'
import {getResponseItem} from 'views/Shared'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {useTimetableCourseClassForm} from 'views/AppLocals/Timetable/useTimetableCourseClassForm'
import {useTimetableDetailMaterialsForm} from 'views/AppLocals/Timetable/useTimetableDetailMaterialsForm'
import {flattenTimetable} from 'views/Workspace/Course/functions/timetableHelpers'
import {PastSessionsSummary} from 'views/Workspace/Course/components/PastSessionsSummary'
import getTitle from 'helpers/getTitle'
import {GoDotFill} from "react-icons/go";
import {formatSimplifyDate} from "../../../../../helpers/dateUtils";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";

// ── Tab "Theo dòng thời gian" ────────────────────────────────────────────────
// Gộp phẳng mọi timetable_details của mọi đợt (flattenTimetable) rồi nhóm theo THÁNG.
// Khác tab "Theo khai báo": ở đây mỗi dòng là 1 BUỔI học cụ thể, không phải 1 đợt/lịch định kỳ.

const TimelineSessionRow = ({session, order, can_edit, onManageMaterials}) => {
    const t = useTranslate()
    const {start_time, end_time, is_holiday, notes, subject, teacher, location_address, materials = []} = session ?? {}
    const isPast = dayjs(start_time).isBefore(dayjs())
    const dateStr = formatSimplifyDate(start_time, '', 'dddd, DD MMMM, YYYY')
    const timeStr = dayjs(start_time).format('HH:mm') !== dayjs(end_time).format('HH:mm')
        ? `${formatDate(start_time, 'HH:mm')} – ${formatDate(end_time, 'HH:mm')}`
        : formatDate(start_time, 'HH:mm')

    return (
        <div className="flex items-start gap-3 px-3 py-2.5">
            <div
                style={{width: 28, height: 28, fontSize: 13}}
                className={classNames(
                    'rounded-full flex flex-center flex-shrink-0 font-bold leading-none',
                    is_holiday === 1 ? 'bg-primary-50 text-primary-500'
                        : isPast ? 'bg-green-100 text-green-600' : 'bg-blue-50 text-blue-600'
                )}>
                {order}
            </div>
            <div className={classNames('flex flex-col flex-1 min-w-0', isPast && 'opacity-80')}>
                <div className="font-semibold text-gray-700 tracking-wide capitalize truncate">
                    {dateStr}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="font-medium text-xs text-color-300 tracking-wider">{timeStr}</span>
                    {is_holiday !== 1 && subject && (
                        <span className="text-xs font-medium text-indigo-500 tracking-wide truncate">
                            {getTitle(subject)}
                        </span>
                    )}
                    {is_holiday !== 1 && teacher && (
                        <span className="text-2xs text-color-400 tracking-wide truncate">
                            {`${t('teachers')}: ${getTitle(teacher)}`}
                        </span>
                    )}
                    {is_holiday !== 1 && location_address && (
                        <span className="text-2xs text-color-400 tracking-wide truncate">
                            <GoDotFill size={8} className="mr-1"/>{location_address}
                        </span>
                    )}
                </div>
                {notes && (
                    <span className="text-xs text-color-400 italic leading-tight">
                        <span className="font-medium text-color-200">{`* ${t('note')}: `}</span>
                        {notes}
                    </span>
                )}
                {materials.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {materials.map((material) => (
                            <a
                                key={material.id}
                                href={material.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-0.5 flex items-center gap-1 rounded-full text-2xs font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100 no-underline truncate max-w-full">
                                <PiLinkBold size={11} className="flex-shrink-0"/>
                                <span className="truncate">{material.title || material.url}</span>
                            </a>
                        ))}
                    </div>
                )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0 gap-1">
                <span className={classNames(
                    'text-xs font-medium px-2 py-0.5 rounded-full whitespace-no-wrap',
                    is_holiday === 1 ? 'bg-primary-50 text-primary-500'
                        : isPast ? 'bg-green-50 text-green-500' : 'bg-blue-50 text-blue-500'
                )}>
                    {is_holiday === 1 ? t('holiday') : isPast ? t('done') : t('upcoming')}
                </span>
                {can_edit && is_holiday !== 1 && (
                    <button
                        type="button"
                        onClick={() => onManageMaterials(session)}
                        title={t('manage lecture materials')}
                        className="flex items-center gap-1 text-2xs font-medium text-color-400 hover:text-indigo-600 whitespace-no-wrap">
                        <PiLinkBold size={11}/>
                        {t('materials')}
                    </button>
                )}
            </div>
        </div>
    )
}

const TimelineView = ({course_class_id, refreshKey, selectedSubjectId, can_edit, onManageMaterials}) => {
    const t = useTranslate()
    const {response, isLoading} = useAsync({
        apiInfo: timetable_getMilestonesByCourseClass_Api,
        query: bindQueryParam({id: course_class_id}),
        deps: [refreshKey],
    })

    const monthGroups = useMemo(() => {
        const data = (_.get(getResponseItem(response), 'data', []) || []).filter((m) => !m?.is_milestone)
        let sessions = flattenTimetable(data)
        if (selectedSubjectId) {
            sessions = sessions.filter((s) => getId(s?.subject) === selectedSubjectId)
        }
        const byMonth = _.groupBy(sessions, (s) => dayjs(s.start_time).format('YYYY-MM'))
        return Object.keys(byMonth)
            .sort()
            .map((key) => ({
                key,
                label: _.capitalize(dayjs(`${key}-01`).format('MMMM YYYY')),
                sessions: byMonth[key],
            }))
    }, [response, selectedSubjectId])

    if (isLoading) {
        return <div className="h-24 background-100 rounded-xl animate-pulse"/>
    }
    if (!monthGroups.length) {
        return <EmptyHolder title={null}/>
    }

    let running = 0
    return (
        <div className="flex flex-col gap-4">
            {monthGroups.map((group) => (
                <div key={group.key} className="flex flex-col border border-color-50 rounded-xl overflow-hidden">
                    <div className="px-3 py-2 background-100 border-b border-color-100 flex items-center gap-2">
                        <PiCalendarBlankFill size={13} className="text-secondary-500"/>
                        <span className="font-bold text-xs text-color-300 uppercase tracking-wider">
                            {group.label}
                        </span>
                        <span className="text-2xs text-color-400">{`(${group.sessions.length})`}</span>
                    </div>
                    <div className="flex flex-col divide-y divide-color-50">
                        {group.sessions.map((session) => {
                            running += 1
                            return (
                                <TimelineSessionRow
                                    key={session.id ?? running}
                                    session={session}
                                    order={running}
                                    can_edit={can_edit}
                                    onManageMaterials={onManageMaterials}
                                />
                            )
                        })}
                    </div>
                </div>
            ))}
        </div>
    )
}

export const CourseClassTimetablePanel = ({item}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const course_class_id = getId(item)
    const can_edit = !!item?.edit
    const subjects = (item?.class_subjects ?? []).map((cs) => cs.subject).filter(Boolean)
    const [selectedSubjectId, setSelectedSubjectId] = useState(null)
    const [timetableRefreshKey, setTimetableRefreshKey] = useState(0)
    // 2 cách xem: 'declaration' = danh sách theo khai báo (từng đợt/lịch định kỳ),
    // 'timeline' = gộp phẳng mọi buổi học của mọi đợt, xếp theo dòng thời gian & nhóm theo tháng.
    const [viewMode, setViewMode] = useState('declaration')

    const [closedItems, setClosedItems] = useState({})
    // Buổi ĐÃ qua trong 1 đợt/lịch định kỳ (timetable_details) mặc định GỘP LẠI thành 1 khối tóm tắt
    // (PastSessionsSummary, dùng CHUNG với MyClassSessionsPanel.js - tab "Sắp tới" của học viên) thay
    // vì liệt kê từng dòng gạch ngang như trước (bạn báo rối mắt khi 1 đợt có nhiều buổi đã học) - bấm
    // mới mở ra xem lại, key theo id đợt (khác hẳn closedItems ở trên - đó là đóng/mở CẢ khối đợt).
    const [expandedPastGroups, setExpandedPastGroups] = useState({})

    const {modal, handleAction, renderContextMenu} = useTimetableCourseClassForm({
        course_class: item,
        refreshKey: timetableRefreshKey,
        setRefreshKey: setTimetableRefreshKey,
    })
    // "Add thẳng trực tiếp vào màn hình link bài giảng" cho TỪNG buổi học (timetable_detail) - độc
    // lập với form sửa Timetable cha ở trên (đúng đợt/lịch định kỳ), mirror useTimetableDetailSongsForm.
    const {modal: materialsModal, handleManageMaterials} = useTimetableDetailMaterialsForm({
        onRefresh: () => setTimetableRefreshKey((k) => k + 1),
    })

    const entityKey = [course_class_id, selectedSubjectId, timetableRefreshKey].join('-')


    const renderItem = useCallback((timetable, index) => {
        if (timetable?.is_milestone) return null

        const {
            from_date, to_date, from_time, to_time,
            subject, teacher, notes, is_holiday,
            timetable_details = [], location_address,
        } = timetable ?? {}

        const dateRange = (from_date && to_date && from_date !== to_date)
            ? `${formatDate(from_date, 'DD/MM')} → ${formatDate(to_date, 'DD/MM/YYYY')}`
            : formatDate(from_date || to_date, 'DD/MM/YYYY')

        const completedCount = timetable_details.filter(detail =>
            dayjs(detail.start_time).isBefore(dayjs())
        ).length

        const totalCount = timetable_details.length

        const isCompleted = totalCount > 0 && completedCount === totalCount

        const isOpen =
            closedItems[getId(timetable)] === undefined
                ? !(isCompleted || is_holiday === 1)
                : !closedItems[getId(timetable)]

        return (
            <div
                key={index}
                className={classNames(
                    "flex flex-col border border-color-50 rounded-xl overflow-hidden transition-all duration-300",
                    isCompleted ? 'opacity-80 hover:opacity-100' : 'hover:shadow-md'
                )}>
                <div
                    className={classNames(
                        "px-3 md:px-4 py-3 flex items-start justify-between gap-2 custom-item-render relative",
                        isOpen ? 'border-b border-color-100 background-100' : 'hover:background-100'
                    )}>
                    <div className="mr-0 md:mr-2 w-full flex flex-col md:flex-row md:items-center gap-x-3 gap-y-1">
                        <div className="flex flex-col flex-1">
                            {subject && (
                                <span
                                    className={classNames(
                                        'font-bold truncate tracking-wide',
                                        isCompleted ? 'text-sm text-gray-700' : 'text-base text-gray-800'
                                    )}>
                                            {getTitle(subject)}
                                        </span>
                            )}
                            <div className="flex flex-col md:flex-row gap-x-3">
                                {teacher && (
                                    <div className="space-x-1 text-xs tracking-wider">
                                        <span className="text-color-400">
                                            {`${t('teachers')}:`}
                                        </span>
                                        <span className="font-semibold text-gray-600">
                                            {getTitle(teacher)}
                                        </span>
                                    </div>
                                )}
                                {location_address && (
                                    <span className="font-medium text-xs text-gray-600 tracking-wider">
                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                        {location_address}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                            {isCompleted && (
                                <span
                                    style={{ padding: '0.125rem 0.75rem', borderRadius: 999 }}
                                    className="w-fit border border-green-300 bg-green-50 font-medium text-xs text-green-600 tracking-wider">
                                {t('finish')} {completedCount}/{totalCount}
                            </span>
                            )}
                            {is_holiday === 1 && (
                                <span
                                    style={{ padding: '0.125rem 0.75rem', borderRadius: 999 }}
                                    className="w-fit border border-primary-300 bg-primary-50 font-medium text-xs text-primary-600 tracking-wider">
                                    {t('holiday')}
                                </span>
                            )}
                            {!isLg && <div className="flex-1"/>}
                            {/*  Time arrange  */}
                            <div className="flex flex-col items-end tracking-wider">
                            <span className="text-2xs text-color-400">
                                {t('time takes place')}
                            </span>
                                <span className="font-semibold text-xs text-gray-700">
                                {dateRange}
                            </span>
                            </div>
                            {/* Khu admin: nút cấu hình luôn hiển thị, đứng cạnh nút sổ xuống,
                                cùng kích thước & khoảng cách - không còn ẩn chờ hover. */}
                            {can_edit && (
                                <div
                                    style={{ width: 28, height: 28, padding: '0.25rem', borderRadius: '0.5rem' }}
                                    className="flex flex-center background-100 hover:background-200 text-color-400">
                                    {renderContextMenu(timetable)}
                                </div>
                            )}
                            {/* Action Open */}
                            <div
                                onClick={() => {
                                    const id = getId(timetable)
                                    setClosedItems(prev => ({
                                        ...prev,
                                        [id]: !prev[id]
                                    }))
                                }}
                                style={{ width: 28, height: 28, padding: '0.25rem', borderRadius: '0.5rem', cursor: 'pointer'}}
                                className="flex flex-center bg-secondary-50 hover:bg-secondary-500 text-secondary-600 hover:text-white">
                                <BsChevronDown
                                    size={14}
                                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {isOpen && (
                    timetable_details.length > 0 ? (
                        <>
                            {notes && (
                                <span className="m-3 p-2 bg-secondary-50 rounded-lg text-xs text-secondary-400 italic leading-tight tracking-wider">
                                    <span className="font-medium text-secondary-600">
                                        {`* ${t('note')}: `}
                                    </span>
                                    {notes}
                                </span>
                            )}
                            {(() => {
                                const now = dayjs()
                                const timetableId = getId(timetable)
                                const isPastExpanded = !!expandedPastGroups[timetableId]
                                // onGoing PHẢI loại trừ khỏi "đã qua" - buổi đang diễn ra hôm nay có
                                // start_time đã trôi qua nên isBefore(now) cũng đúng, nếu không trừ sẽ
                                // bị gộp nhầm vào khối tóm tắt "đã qua" dù thực ra chưa kết thúc.
                                const getFlags = (detail) => {
                                    const startTime = dayjs(detail.start_time)
                                    const endTime = dayjs(detail.end_time)
                                    const onGoing = now.isSame(startTime, 'day') && !now.isBefore(startTime) && !now.isAfter(endTime)
                                    return {onGoing, isPast: !onGoing && startTime.isBefore(now)}
                                }
                                const entries = timetable_details.map((detail, di) => ({detail, di, ...getFlags(detail)}))
                                const pastEntries = entries.filter((e) => e.isPast)
                                const restEntries = entries.filter((e) => !e.isPast)

                                // Buổi ĐÃ qua trong đợt này gộp lại thành 1 khối tóm tắt gọn (thay vì
                                // liệt kê từng dòng gạch ngang) - bấm mới mở ra xem, xem PastSessionsSummary
                                // (dùng chung với MyClassSessionsPanel.js, tab "Sắp tới" học viên).
                                // Trong đợt này mà thôi, cùng dữ liệu KHÔNG ẩn hẳn - chỉ thu gọn mặc định.
                                const renderPastRow = ({detail, di}) => {
                                    const dateStr = formatSimplifyDate(detail.start_time, '', 'dddd, DD MMMM, YYYY')
                                    const timeDetail = dayjs(detail.start_time).format('HH:mm') !== dayjs(detail.end_time).format('HH:mm') ?
                                        `${formatDate(detail.start_time, 'HH:mm')} – ${formatDate(detail.end_time, 'HH:mm')}` :
                                        formatDate(detail.start_time, 'HH:mm')
                                    // "Inactive" - xám nhạt toàn bộ (số thứ tự/chữ/badge), KHÔNG còn
                                    // gạch ngang như trước (bạn báo gạch ngang khó đọc/rối mắt).
                                    return (
                                        <div key={di} className="flex items-center gap-3 px-3 py-2 opacity-70">
                                            <div
                                                style={{width: 28, height: 28, fontSize: 13}}
                                                className="rounded-full flex flex-center flex-shrink-0 font-bold leading-none background-100 text-color-300">
                                                {di + 1}
                                            </div>
                                            <div className="flex flex-col flex-1 min-w-0">
                                                <span className="text-sm font-medium text-color-300 tracking-wide truncate capitalize">
                                                    {dateStr}
                                                </span>
                                                <span className="text-xs text-color-400 tracking-wider">
                                                    {timeDetail}
                                                </span>
                                            </div>
                                            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-2xs font-medium background-100 text-color-300 whitespace-no-wrap">
                                                {is_holiday === 1 ? t('holiday') : t('done')}
                                            </span>
                                        </div>
                                    )
                                }

                                return (
                                    <div className="flex flex-col divide-y divide-color-50">
                                        {pastEntries.length > 0 && (
                                            <PastSessionsSummary
                                                count={pastEntries.length}
                                                totalSessions={timetable_details.length}
                                                lastStudiedDate={formatSimplifyDate(
                                                    pastEntries[pastEntries.length - 1]?.detail?.end_time, '', 'DD/MM/YYYY'
                                                )}
                                                expanded={isPastExpanded}
                                                onToggle={() => setExpandedPastGroups((prev) => ({
                                                    ...prev,
                                                    [timetableId]: !prev[timetableId],
                                                }))}
                                            />
                                        )}
                                        {isPastExpanded && pastEntries.map(renderPastRow)}
                                        {restEntries.map(({detail, di, onGoing, isPast}) => {
                                            const dateStr = formatSimplifyDate(detail.start_time, '', 'dddd, DD MMMM, YYYY')
                                            const timeDetail = dayjs(detail.start_time).format('HH:mm') !== dayjs(detail.end_time).format('HH:mm') ?
                                                `${formatDate(detail.start_time, 'HH:mm')} – ${formatDate(detail.end_time, 'HH:mm')}` :
                                                formatDate(detail.start_time, 'HH:mm')

                                            return (
                                                <div
                                                    key={di}
                                                    className="flex items-center gap-3 px-3 py-2">
                                                    <div
                                                        style={{ width: 28, height: 28, fontSize: 14}}
                                                        className={classNames(
                                                            'rounded-full flex flex-center flex-shrink-0 font-bold leading-none',
                                                            onGoing ? 'bg-blue-500 text-white' : isPast ? 'bg-green-100 text-green-600' : 'background-200 text-color-200'
                                                        )}>
                                                        {di + 1}
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            "flex flex-col flex-1 min-w-0",
                                                            onGoing ? 'font-bold text-blue-700' : isPast ? 'line-through opacity-80' : 'font-semibold text-gray-700 '
                                                        )}>
                                                        <div className="tracking-wide truncate capitalize">
                                                            {dateStr}
                                                        </div>
                                                        <span className="font-medium text-xs text-color-300 tracking-wider">
                                                            {timeDetail}
                                                        </span>

                                                        {detail?.notes && (
                                                            <span className="text-xs text-color-400 italic leading-tight">
                                                                <span className="font-medium text-color-200">
                                                                    {`* ${t('note')}: `}
                                                                </span>
                                                                {detail?.notes}
                                                            </span>
                                                        )}
                                                        {/* Link tài liệu bài giảng của buổi này - luôn có sẵn ở
                                                            detail.materials (transformer trả thẳng, không opt-in) -
                                                            học viên bấm mở tab mới, admin bấm nút bút chì cạnh bên
                                                            để thêm/sửa/xoá (xem TimetableDetailMaterialsForm.js). */}
                                                        {(detail?.materials ?? []).length > 0 && (
                                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                                {detail.materials.map((material) => (
                                                                    <a
                                                                        key={material.id}
                                                                        href={material.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="px-2 py-0.5 flex items-center gap-1 rounded-full text-2xs font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100 no-underline truncate max-w-full">
                                                                        <PiLinkBold size={11} className="flex-shrink-0"/>
                                                                        <span className="truncate">{material.title || material.url}</span>
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end flex-shrink-0 gap-1">
                                                        <span className={classNames(
                                                            'font-medium px-2 py-0.5 rounded-full whitespace-no-wrap',
                                                            onGoing ? 'bg-blue-500 text-xs text-white' : isPast ? 'bg-green-50 text-2xs text-green-500' : 'bg-blue-50 text-2xs text-blue-500'
                                                        )}>
                                                            {onGoing ? t('ongoing') : isPast ? t('done') : t('upcoming')}
                                                        </span>
                                                        {can_edit && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleManageMaterials(detail)
                                                                }}
                                                                title={t('manage lecture materials')}
                                                                className="flex items-center gap-1 text-2xs font-medium text-color-400 hover:text-indigo-600 whitespace-no-wrap">
                                                                <PiLinkBold size={11}/>
                                                                {t('materials')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            })()}
                        </>
                    ) : null
                )}
            </div>
        )
    }, [t, can_edit, renderContextMenu, closedItems, handleManageMaterials, expandedPastGroups])

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-col flex-1">
                    <div className="font-bold text-base text-slate-600 tracking-wide">
                        {t('timetable for the class')}
                    </div>
                    <div style={{ height: 3 }} className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                {can_edit && (
                    <div className="flex justify-end">
                        <Button
                            color="primary" variant="filled"
                            className="rounded-md no-border font-medium text-xs"
                            onClick={() => handleAction(TimetableActionTypes.TIMETABLE_CREATE)}>
                            {t('add session')}
                        </Button>
                    </div>
                )}
            </div>
            {/* Toggle 2 cách xem: theo khai báo (đợt/lịch định kỳ) vs theo dòng thời gian (từng buổi) */}
            <div className="flex items-center flex-shrink-0 w-fit rounded-lg border border-color-200 overflow-hidden">
                {[
                    {key: 'declaration', label: t('by schedule declaration'), icon: <PiListBulletsBold size={13}/>},
                    {key: 'timeline', label: t('by timeline'), icon: <PiCalendarBlankFill size={13}/>},
                ].map(({key, label, icon}, i) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setViewMode(key)}
                        className={classNames(
                            'px-3 py-1.5 flex items-center gap-1.5 text-xs font-medium transition-colors',
                            i > 0 && 'border-l border-color-200',
                            viewMode === key
                                ? 'bg-secondary-500 text-white'
                                : 'background text-color-300 hover:text-secondary-600'
                        )}>
                        {icon}
                        {label}
                    </button>
                ))}
            </div>
            <div className="flex flex-col gap-1.5">
                <span className="text-color-300 tracking-wider">{`${t('subjects')} :`}</span>
                <div className="flex flex-wrap gap-1.5">
                    {subjects.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => setSelectedSubjectId(null)}
                                className={classNames(
                                    'px-3 py-1 text-xs font-medium rounded-lg border transition-all duration-300',
                                    selectedSubjectId === null
                                        ? 'bg-indigo-600 border-indigo-600 text-white'
                                        : 'bg-transparent border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                                )}>
                                {t('all')}
                            </button>
                            {subjects.map(s => (
                                <button
                                    key={getId(s)}
                                    onClick={() => setSelectedSubjectId(getId(s))}
                                    className={classNames(
                                        'px-3 py-1 text-xs font-medium rounded-lg border transition-all duration-300',
                                        selectedSubjectId === getId(s)
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'bg-transparent border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                                    )}>
                                    {s?.title}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {viewMode === 'declaration' ? (
                <EntityList
                    key={entityKey}
                    refreshKey={entityKey}
                    RenderEntity={React.Fragment}
                    renderEmpty={() => <EmptyHolder title={null}/>}
                    renderItem={renderItem}
                    values={selectedSubjectId ? {subject_id: selectedSubjectId} : {}}
                    apiInfo={timetable_getMilestonesByCourseClass_Api}
                    query={bindQueryParam({id: course_class_id})}
                    renderWrap={(child) => (
                        <div className="flex flex-col gap-4">
                            {child}
                        </div>
                    )}
                />
            ) : (
                <TimelineView
                    course_class_id={course_class_id}
                    refreshKey={timetableRefreshKey}
                    selectedSubjectId={selectedSubjectId}
                    can_edit={can_edit}
                    onManageMaterials={handleManageMaterials}
                />
            )}
            {modal}
            {materialsModal}
        </div>
    )
}
